import React, {
    KeyboardEventHandler,
    memo,
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react';
import {
    Container,
    Input,
    Search,
    SearchContainer,
    Separator,
    StyledCloseIcon,
    StyledSearch16
} from './HBSearchInput.styles';
// eslint-disable-next-line import-x/no-extraneous-dependencies
import { useEvent, usePrevious } from 'react-use';
import { MustIncludeOneOfBasicTestIdProps } from '../common/internal/test-id.types';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';

export interface Props {
    /** Called with the current query on Enter key or 'Search' button click. Also fires on blur if `shouldSearchOnBlur` is true, and on clear if `shouldSearchOnClear` is true. */
    onSearch: (
        searchQuery: string
    ) => void | KeyboardEventHandler<HTMLInputElement>;
    /** Controlled query value; reflects the current input text. */
    searchQuery: string;
    /** State setter for `searchQuery`; called on every keystroke. */
    setSearchQuery: (query: string) => void;
    /** Placeholder text shown when the input is empty. */
    placeholder?: string;
    /** When true, calls `onSearch` with an empty string after the user clears the input. */
    shouldSearchOnClear?: boolean;
    /** When true, calls `onSearch` with the current query when the input loses focus. */
    shouldSearchOnBlur?: boolean;
    /** CSS class applied to the root element. */
    className?: string;
}

const HBSearchInput = ({
    testIdName,
    testId,
    onSearch,
    searchQuery,
    setSearchQuery,
    placeholder,
    shouldSearchOnClear = false,
    shouldSearchOnBlur = false,
    className
}: Props & MustIncludeOneOfBasicTestIdProps) => {
    const inputRef = useRef<HTMLInputElement>();
    const [isInputFocus, setIsInputFocus] = useState(false);
    const previousIsInputFocus = usePrevious(isInputFocus);

    useEvent(
        'click',
        useCallback(() => {
            const isFocus = inputRef.current === document.activeElement;
            setIsInputFocus(isFocus);
        }, [])
    );

    useEffect(() => {
        if (
            !isInputFocus &&
            previousIsInputFocus &&
            searchQuery &&
            shouldSearchOnBlur
        ) {
            onSearch(searchQuery);
        }
    }, [
        isInputFocus,
        previousIsInputFocus,
        searchQuery,
        shouldSearchOnBlur,
        onSearch
    ]);

    const onChange = useCallback(
        e => setSearchQuery(e.target.value),
        [setSearchQuery]
    );

    const onClearSearch = useCallback(() => {
        setSearchQuery('');
        if (shouldSearchOnClear) {
            onSearch('');
        }
    }, [onSearch, setSearchQuery, shouldSearchOnClear]);

    const handleSearch = useCallback(() => {
        onSearch(searchQuery);
    }, [onSearch, searchQuery]);

    const focusInput = () => inputRef.current.focus();

    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName,
        suffix: 'search-input',
        componentName: 'HBSearchInput'
    });
    return (
        <Container className={className} onClick={focusInput}>
            <StyledSearch16 />
            <Input
                ref={inputRef}
                value={searchQuery}
                onChange={onChange}
                onEnterKey={handleSearch}
                placeholder={placeholder}
                testId={finalTestId}
            />
            {!!searchQuery && (
                <SearchContainer>
                    <StyledCloseIcon
                        onClick={onClearSearch}
                        data-testid={
                            finalTestId ? `${finalTestId}-clear` : undefined
                        }
                    />
                    {isInputFocus && (
                        <>
                            <Separator />
                            <Search
                                type="emphasis14"
                                color="text.accent"
                                clickable
                                onClick={handleSearch}
                                data-testid={
                                    finalTestId
                                        ? `${finalTestId}-search`
                                        : undefined
                                }
                            >
                                Search
                            </Search>
                        </>
                    )}
                </SearchContainer>
            )}
        </Container>
    );
};

export default memo(HBSearchInput);
